import { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { apiFetch } from "../../utils/api";
import { Unlink, Search, Users as UsersIcon, Loader2, Link2, Settings, AlertTriangle, CheckCircle2, Filter } from "lucide-react";
import { Checkbox } from "../../components/ui/checkbox";
import { PLATFORMS, PLATFORM_BY_KEY, formatNumber } from "./common";
import { SocialDataSkeleton } from "./SocialSkeleton";

function AdAccountsFilterDialog({ open, onOpenChange, clientId, clientName, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/zernio-admin/ad-accounts?client_id=${clientId}`);
      if (r.ok) {
        const d = await r.json();
        setData(d);
        setSelected(new Set(d.selected_ids || []));
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore caricamento ad accounts");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, clientId]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiFetch("/zernio-admin/ad-accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, ad_account_ids: Array.from(selected) }),
      });
      if (r.ok) {
        toast.success(selected.size ? "Filtro Ad Account salvato" : "Filtro rimosso (mostra tutti)");
        onSaved && onSaved();
        onOpenChange(false);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
      }
    } finally { setSaving(false); }
  };

  const clearAll = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set((data?.ad_accounts || []).map(a => a.account_id)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Filter className="w-4 h-4" /> Filtro Ad Accounts · {clientName}</DialogTitle>
          <DialogDescription>
            Se il profilo Zernio ha più Ad Account collegati (di clienti diversi), seleziona solo quelli che appartengono davvero a questo cliente. Se non selezioni nulla, la dashboard mostrerà tutte le campagne del profilo.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="text-center text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Caricamento...</div>}
        {!loading && data && (
          <>
            <div className="flex items-center gap-2 pb-2 border-b">
              <div className="text-xs text-slate-500">{data.ad_accounts.length} Ad Account trovati</div>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={clearAll} data-testid="ad-accounts-clear">Nessuno</Button>
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="ad-accounts-all">Tutti</Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {data.ad_accounts.length === 0 && (
                <div className="text-center text-sm text-slate-500 py-6">
                  Nessun Ad Account trovato per il profilo. Assicurati di aver collegato Meta Ads su Zernio.
                </div>
              )}
              {data.ad_accounts.map(a => (
                <label key={a.account_id} className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer hover:bg-slate-50 ${selected.has(a.account_id) ? "border-blue-300 bg-blue-50" : ""}`} data-testid={`ad-account-${a.account_id}`}>
                  <Checkbox checked={selected.has(a.account_id)} onCheckedChange={() => toggle(a.account_id)} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{a.platform_ad_account_name || a.platform_ad_account_id || a.account_id}</div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">{a.platform_ad_account_id} · {a.platform || "-"}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 tabular-nums whitespace-nowrap">{a.campaigns_count} campagne</div>
                </label>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="ad-accounts-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Salva ({selected.size} selezionati)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ZernioProfilesManager({ clients, open, onOpenChange, onLinked }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selections, setSelections] = useState({}); // profile_id -> client_id
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState("unlinked"); // all | unlinked | linked
  const [adFilterFor, setAdFilterFor] = useState(null); // { client_id, client_name }
  const [autoSelecting, setAutoSelecting] = useState(false);

  const runAutoSelect = async () => {
    if (!window.confirm("Per ogni cliente già collegato senza filtro Ad Account: se il profilo ha un solo Ad Account, verrà impostato automaticamente. Procedere?")) return;
    setAutoSelecting(true);
    try {
      const r = await apiFetch("/zernio-admin/auto-select-single-account", { method: "POST" });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Elaborati ${d.processed}: ${d.auto_selected} configurati, ${d.needs_manual_choice} richiedono scelta manuale, ${d.no_ads_found} senza ads.`);
        onLinked && onLinked();
        load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
      }
    } finally { setAutoSelecting(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/zernio-admin/profiles");
      if (r.ok) {
        const d = await r.json();
        setProfiles(d.profiles || []);
      } else {
        toast.error("Errore caricamento profili Zernio");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const linkProfile = async (profile_id, client_id) => {
    if (!client_id) return;
    setSavingId(profile_id);
    try {
      const r = await apiFetch("/zernio-admin/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile_id, client_id }),
      });
      if (r.ok) {
        toast.success("Profilo collegato");
        await load();
        onLinked && onLinked();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore collegamento");
      }
    } finally { setSavingId(null); }
  };

  const unlinkProfile = async (profile_id, client_id) => {
    if (!window.confirm("Scollegare il profilo Zernio da questo cliente?")) return;
    setSavingId(profile_id);
    try {
      const r = await apiFetch("/zernio-admin/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile_id: null, client_id }),
      });
      if (r.ok) {
        toast.success("Profilo scollegato");
        await load();
        onLinked && onLinked();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
      }
    } finally { setSavingId(null); }
  };

  const filtered = profiles.filter(p => filter === "all" ? true : filter === "unlinked" ? !p.linked_client : !!p.linked_client);
  const unlinkedCount = profiles.filter(p => !p.linked_client).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="w-4 h-4" /> Gestione profili Zernio</DialogTitle>
          <DialogDescription>
            Assegna un profilo Zernio (con account social e/o Meta Ads collegati) al cliente giusto della dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 border-b pb-3">
          {[
            { v: "unlinked", l: `Non collegati (${unlinkedCount})` },
            { v: "linked", l: `Collegati (${profiles.length - unlinkedCount})` },
            { v: "all", l: `Tutti (${profiles.length})` },
          ].map(t => (
            <button key={t.v} onClick={() => setFilter(t.v)}
              className={`px-3 py-1 text-xs rounded-full border transition ${filter === t.v ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              data-testid={`zernio-profiles-filter-${t.v}`}>
              {t.l}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={runAutoSelect} disabled={autoSelecting} data-testid="zernio-profiles-autoselect">
            {autoSelecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Filter className="w-3.5 h-3.5 mr-1" />}
            Auto-configura Ad Accounts
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="zernio-profiles-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ricarica"}
          </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading && <div className="text-center text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Caricamento...</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-6">Nessun profilo in questa categoria.</div>
          )}
          {!loading && filtered.map(p => (
            <div key={p.profile_id} className="border rounded-lg p-3 flex items-center gap-3" data-testid={`zernio-profile-${p.profile_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-2">
                  {p.name || "(senza nome)"}
                  {p.linked_client
                    ? <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> {p.linked_client.client_name}</span>
                    : <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Non collegato</span>}
                </div>
                <div className="text-[10px] text-slate-400 font-mono">{p.profile_id}</div>
              </div>
              {!p.linked_client ? (
                <div className="flex items-center gap-2">
                  <Select value={selections[p.profile_id] || ""} onValueChange={(v) => setSelections({ ...selections, [p.profile_id]: v })}>
                    <SelectTrigger className="w-56 h-8 text-xs" data-testid={`zernio-profile-select-${p.profile_id}`}>
                      <SelectValue placeholder="Seleziona cliente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id} className="text-xs">{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => linkProfile(p.profile_id, selections[p.profile_id])}
                    disabled={!selections[p.profile_id] || savingId === p.profile_id}
                    data-testid={`zernio-profile-link-${p.profile_id}`}>
                    {savingId === p.profile_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Collega"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAdFilterFor({ client_id: p.linked_client.client_id, client_name: p.linked_client.client_name })} data-testid={`zernio-profile-adfilter-${p.profile_id}`}>
                    <Filter className="w-3 h-3 mr-1" /> Ad Accounts
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => unlinkProfile(p.profile_id, p.linked_client.client_id)}
                    disabled={savingId === p.profile_id}
                    data-testid={`zernio-profile-unlink-${p.profile_id}`}>
                    {savingId === p.profile_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Unlink className="w-3 h-3 mr-1" /> Scollega</>}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
      <AdAccountsFilterDialog
        open={!!adFilterFor}
        onOpenChange={(o) => !o && setAdFilterFor(null)}
        clientId={adFilterFor?.client_id}
        clientName={adFilterFor?.client_name}
        onSaved={() => { onLinked && onLinked(); }}
      />
    </Dialog>
  );
}

export function ConnectionsTab({ clients, accountsByClient, fetchAll, loading }) {
  const [search, setSearch] = useState("");
  const [connecting, setConnecting] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);

  const startConnect = async (platform, clientId) => {
    setConnecting({ clientId, platform });
    try {
      const r = await apiFetch(`/zernio/connect/${platform}?client_id=${clientId}`);
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore avvio collegamento");
        setConnecting(null);
        return;
      }
      const data = await r.json();
      window.location.href = data.auth_url;
    } catch {
      toast.error("Errore connessione");
      setConnecting(null);
    }
  };

  const disconnect = async (clientId, accountId, label) => {
    if (!window.confirm(`Scollegare ${label} da questo cliente?`)) return;
    const r = await apiFetch(`/zernio/accounts/${clientId}/${accountId}`, { method: "DELETE" });
    if (r.ok) { toast.success("Scollegato"); fetchAll(); }
    else toast.error("Errore disconnessione");
  };

  const filtered = clients.filter(c => !search || (c.name || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Cerca cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="social-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => setManagerOpen(true)} data-testid="zernio-profiles-open">
          <Settings className="w-3.5 h-3.5 mr-1.5" /> Gestione profili Zernio
        </Button>
      </div>

      <ZernioProfilesManager
        clients={clients}
        open={managerOpen}
        onOpenChange={setManagerOpen}
        onLinked={fetchAll}
      />

      {loading ? (
        <SocialDataSkeleton variant="list" rows={4} />
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="social-empty">Nessun cliente trovato.</Card>
      ) : (
        <div className="grid gap-5">
          {filtered.map((c) => {
            const accs = accountsByClient[c.client_id] || [];
            const connectedPlatforms = new Set(accs.map(a => a.platform));
            return (
              <Card key={c.client_id} className="p-0 overflow-hidden shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid={`social-card-${c.client_id}`}>
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-48 bg-gradient-to-br from-slate-900 to-slate-700 text-white p-4 flex flex-col justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-300 font-medium">Cliente</div>
                      <h3 className="font-semibold text-base mt-1 leading-tight">{c.name}</h3>
                    </div>
                    <div className="text-[11px] text-slate-300 mt-3">{accs.length} {accs.length === 1 ? "account" : "account"} collegati</div>
                  </div>
                  <div className="flex-1 p-4 space-y-3">
                    {accs.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {accs.map((a) => {
                          const meta = PLATFORM_BY_KEY[a.platform];
                          return (
                            <div key={a.account_id} className={`border rounded-lg p-2.5 text-xs flex items-center gap-2.5 min-w-[200px] ${meta?.border || ''} ${meta?.bg || 'bg-white'}`} data-testid={`zernio-account-${a.account_id}`}>
                              {a.profile_picture_url ? <img src={a.profile_picture_url} alt="" className="w-9 h-9 rounded-full" /> : <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${meta?.gradient || 'from-slate-400 to-slate-600'} flex items-center justify-center text-white`}>{meta?.icon ? <meta.icon className="w-4 h-4" /> : <span className="font-bold text-[10px]">TT</span>}</div>}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{a.username ? `@${a.username}` : (a.display_name || "Account")}</div>
                                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                  <span className="flex items-center gap-0.5"><UsersIcon className="w-2.5 h-2.5" />{formatNumber(a.followers_count)}</span>
                                  {a.media_count != null && <span>·  {a.media_count} post</span>}
                                </div>
                              </div>
                              <button onClick={() => disconnect(c.client_id, a.account_id, a.username || a.platform)} className="text-red-400 hover:text-red-600 p-1" data-testid={`zernio-disconnect-${a.account_id}`}>
                                <Unlink className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap pt-1">
                      {PLATFORMS.map(p => connectedPlatforms.has(p.key) ? null : (
                        <Button
                          key={p.key}
                          variant="outline"
                          size="sm"
                          onClick={() => startConnect(p.key, c.client_id)}
                          disabled={connecting && connecting.clientId === c.client_id && connecting.platform === p.key}
                          className={`text-xs ${p.border} ${p.textHex} hover:${p.bg}`}
                          data-testid={`zernio-connect-${p.key}-${c.client_id}`}
                        >
                          {connecting && connecting.clientId === c.client_id && connecting.platform === p.key ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <><Link2 className="w-3 h-3 mr-1" />Collega</>} <span className="ml-1">{p.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
